import { useContext, useMemo, useState } from "react";
import { RiskRatingDetailsModal } from "@jsq/compliance-components";
import { Flex, Text } from "@jsq/ds/core";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { useGetRiskRatingDetailsQuery } from "~/graphql/supergraphHooks";
import {
  type GetCaseInvestingProfileRiskRatingQuery,
  type GetComplianceCaseQuery,
  type RiskRating,
} from "~/graphql/supergraphTypes";
import {
  isEntityClientOnboardingCaseType,
  mapCaseTypeToText,
} from "~/pages/Compliance/ComplianceTable/CaseTypeRenderer/utils";
import {
  useAmlLuxFeatureFlag,
  useComplianceEntityOnboardingEnabled,
  useIOReusableProfiles,
} from "~/pages/Compliance/flags";
import { ComplianceRulesContext } from "../../complianceRulesContext";
import { CaseStatusRenderer } from "../../ComplianceTable/CaseStatusRenderer";
import { ResolutionRenderer } from "../../ComplianceTable/ResolutionRenderer";
import { RISK_RATING_PROPS } from "../../riskRating/constants";
import { RiskRatingCell } from "../../riskRating/RiskRatingCell";
import { RiskRatingProps } from "../../riskRating/types";
import { getRiskRating } from "../../riskRating/utils";
import { isStandaloneCaseType } from "../../utils";
import {
  escalationTypeLabel,
  isEscalationRevampCaseType,
  isInternalEscalationSource,
} from "../../utils/constants";
import { showsPepExposure } from "../pepExposure/constants";
import { PepExposureValue } from "../pepExposure/PepExposureValue";
import { getCasePepExposure } from "../pepExposure/utils";
import { DomicileRenderer } from "../Profiles/DomicileRenderer";
import { getCountryName } from "../Profiles/utils";
import { ComplianceProgramDisplay } from "./ComplianceProgramDisplay";
import { ComplianceProgramDisplayV2 } from "./ComplianceProgramDisplay/V2";
import { buildRiskRatingDetailsProps } from "./riskRatingDetails";
import SecondaryDetail from "./SecondaryDetail";
import { caseDetailsSummaryItem, headerChrome, headerSummaryRow } from "./styles_vanilla.css";
import SubscriptionLink from "./SubscriptionLink";
import { SourceCaseType } from "./types";
import {
  getDisplayRiskRating,
  getHeaderCasePrograms,
  getHeaderTitle,
  isVisibleSubtitleDetail,
} from "./utils";

type Props = {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
  caseRiskRatings: GetCaseInvestingProfileRiskRatingQuery["complianceCase"] | undefined;
};

const CaseDetailsHeader = ({ complianceCase, caseRiskRatings }: Props) => {
  const rules = useContext(ComplianceRulesContext);
  const [showRiskRatingDetailsModal, setShowRiskRatingDetailsModal] = useState(false);
  const riskRatingDetails = useGetRiskRatingDetailsQuery({
    variables: { caseId: complianceCase?.id ?? 0 },
    skip: !complianceCase?.id,
  });
  const riskRatingDetailsProps = useMemo(
    () =>
      buildRiskRatingDetailsProps(riskRatingDetails.data?.complianceCase, rules.usesProfiles),
    [riskRatingDetails.data?.complianceCase, rules.usesProfiles],
  );
  const isRecurringScreening = complianceCase?.caseType === ComplianceCaseTypeEnum.RecurringHit;
  const isEscalationCase = complianceCase?.caseType === ComplianceCaseTypeEnum.Escalation;
  const isStandaloneCase = isStandaloneCaseType(
    complianceCase?.caseType as ComplianceCaseTypeEnum,
  );
  const sourceCaseType = complianceCase?.sourceCase?.caseType as
    ComplianceCaseTypeEnum | undefined;
  const hideDomicileAndRisk = isEscalationCase && isEscalationRevampCaseType(sourceCaseType);
  const riskRatingInfo = caseRiskRatings?.investingProfile?.riskRating;
  const profileRiskRating = getRiskRating(riskRatingInfo, complianceCase?.programId);
  const riskRating: RiskRating = getDisplayRiskRating(
    complianceCase?.programId,
    caseRiskRatings?.riskRating,
    profileRiskRating,
  );
  const cellProps: RiskRatingProps = RISK_RATING_PROPS[riskRating];
  const showLinkedPositionForCaseType = [
    ComplianceCaseTypeEnum.InvestorOnboarding,
    ComplianceCaseTypeEnum.Remediation,
  ];
  const caseType = complianceCase?.caseType as ComplianceCaseTypeEnum | undefined;
  const isClientOnboarding = caseType === ComplianceCaseTypeEnum.ClientOnboarding;
  const isEntityClientOnboarding = !!caseType && isEntityClientOnboardingCaseType(caseType);
  const showPositions = showLinkedPositionForCaseType.includes(
    caseType as ComplianceCaseTypeEnum,
  );
  const resolution = complianceCase?.resolution ?? "";
  const amlLuxEnabled = useAmlLuxFeatureFlag();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const casePrograms = getHeaderCasePrograms(
    caseType,
    complianceCase?.casePrograms,
    complianceCase?.program,
  );
  const showPepExposure =
    rules.usesProfiles && !hideDomicileAndRisk && showsPepExposure(caseType);
  const casePepExposure = getCasePepExposure(complianceCase);

  const secondaryDetailsConfig = [
    {
      key: "case-type",
      condition: true,
      "data-testid": "case-type",
      detail: mapCaseTypeToText(caseType),
      sourceCase: complianceCase?.sourceCase as SourceCaseType,
    },
    {
      key: "escalation-type",
      condition: isEscalationCase && internalEscalationsEnabled,
      "data-testid": "compliance-case-escalation-type",
      detail: `${escalationTypeLabel(sourceCaseType)} escalation`,
    },
    {
      key: "arena-name",
      condition: !isRecurringScreening && !isClientOnboarding,
      "data-testid": "compliance-case-arena-name",
      detail: complianceCase?.externalArena?.name,
    },
    {
      key: "entity-name",
      condition:
        rules.usesProfiles &&
        !isRecurringScreening &&
        !isEscalationCase &&
        !isEntityClientOnboarding,
      "data-testid": "compliance-case-entity-name",
      detail: complianceCase?.externalEntity?.name,
    },
    {
      key: "position-name",
      condition: showPositions,
      "data-testid": "compliance-case-position-name",
      detail: "Position",
      positionProps: {
        positionId: complianceCase?.position?.id ?? null,
        arenaId: complianceCase?.externalArena?.objectId ?? null,
        entityId: complianceCase?.externalEntity?.objectId ?? null,
        entityName: complianceCase?.externalEntity?.name ?? null,
      },
    },
    {
      key: "program-name",
      condition: !rules.usesProfiles && !isRecurringScreening && !isEscalationCase,
      "data-testid": "program-name",
      detail: "Payments",
    },
    {
      key: "case-id",
      condition: true,
      "data-testid": "compliance-case-id",
      detail: complianceCase?.id,
    },
  ];

  return (
    <>
      <div className={headerChrome}>
        <div>
          <Text variant="h2" data-testid="compliance-case-profile-name">
            {getHeaderTitle(complianceCase, rules.usesProfiles)}
          </Text>
          <Flex alignItems="center" data-testid="case-details-subtitle">
            {secondaryDetailsConfig
              .filter((config) => config.condition && isVisibleSubtitleDetail(config.detail))
              .map((config, index, visibleDetails) => (
                <SecondaryDetail
                  key={config.key}
                  data-testid={config["data-testid"]}
                  detail={config.detail}
                  sourceCase={config.sourceCase}
                  positionProps={config.positionProps}
                  showSeparator={index < visibleDetails.length - 1}
                />
              ))}

            {rules.usesProfiles && (
              <SubscriptionLink
                arena={complianceCase?.externalArena}
                object={complianceCase?.externalObject}
              />
            )}
          </Flex>
        </div>
        <Flex data-cy="case-details-header" className={headerSummaryRow}>
          <div className={caseDetailsSummaryItem}>
            <CaseStatusRenderer
              params={{
                value: complianceCase?.status ?? "",
              }}
              showIcon
            />
            <Text variant="secondary-md">Status</Text>
          </div>
          <div className={caseDetailsSummaryItem}>
            <ResolutionRenderer
              resolution={resolution}
              status={complianceCase?.status}
              internal={
                isEntityClientOnboarding ||
                (isEscalationCase && isInternalEscalationSource(sourceCaseType))
              }
            />
            <Text variant="secondary-md">Resolution</Text>
          </div>
          {rules.usesProfiles && !hideDomicileAndRisk && (
            <div className={caseDetailsSummaryItem}>
              <DomicileRenderer
                params={{
                  value: getCountryName(complianceCase?.investingProfile?.addresses || []),
                }}
                variant="header"
              />
              <Text variant="secondary-md">Domicile</Text>
            </div>
          )}
          {rules.usesProfiles && !hideDomicileAndRisk && !isStandaloneCase && (
            <div className={caseDetailsSummaryItem}>
              <RiskRatingCell
                hasIcon={cellProps.hasIcon}
                iconType={cellProps.iconType}
                color={cellProps.color}
                text={cellProps.text}
                onInspectRiskRating={
                  cellProps.hasIcon
                    ? () => {
                        setShowRiskRatingDetailsModal(true);
                      }
                    : undefined
                }
              />
              <Text variant="secondary-md">Risk Rating</Text>
            </div>
          )}
          {showPepExposure && (
            <div className={caseDetailsSummaryItem} data-testid="pep-exposure-summary">
              <PepExposureValue pepCategory={casePepExposure} />
              <Text variant="secondary-md">PEP exposure</Text>
            </div>
          )}
          {amlLuxEnabled && casePrograms.length > 0 && (
            <div className={caseDetailsSummaryItem} data-testid="compliance-program-summary">
              {ioReusableProfilesEnabled ? (
                <ComplianceProgramDisplayV2
                  casePrograms={casePrograms}
                  complianceCase={complianceCase}
                />
              ) : (
                <ComplianceProgramDisplay casePrograms={casePrograms} />
              )}
              <Text variant="secondary-md">Compliance program</Text>
            </div>
          )}
        </Flex>
      </div>
      <RiskRatingDetailsModal
        show={showRiskRatingDetailsModal}
        onClose={() => setShowRiskRatingDetailsModal(false)}
        ratings={riskRatingDetailsProps.ratings}
        positionName={riskRatingDetailsProps.positionName}
        entityName={riskRatingDetailsProps.entityName}
      />
    </>
  );
};

export default CaseDetailsHeader;
